import * as React from 'react'

import { cn } from '@/lib/css-utils'

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & {
    containerClassName?: string
  }
>(({ className, containerClassName, ...props }, ref) => (
  <div
    className={cn(
      'relative w-full overflow-auto border border-table-border rounded-lg shadow-xs',
      containerClassName,
    )}
  >
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-md', className)}
      {...props}
    />
  </div>
))
Table.displayName = 'Table'

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
))
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-b-0', className)}
    {...props}
  />
))
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t bg-muted/50 font-medium last:[&>tr]:border-b-0 border-table-border',
      'flex justify-center w-full',
      className,
    )}
    {...props}
  />
))
TableFooter.displayName = 'TableFooter'

const tableStyles = {
  row: {
    base: 'border-b border-table-border transition-colors hover:bg-table-tr-hover-bg \
      data-[state=selected]:bg-table-tr-selected-bg transition \
      cursor-pointer border-l-2 border-l-[transparent]',
    selected:
      'bg-table-tr-selected-bg/5 hover:bg-table-tr-selected-bg/5 \
       border-l-2  border-l-table-tr-selected-border',
  },
}

export function getTableRowClassName(opts?: {
  isSelected?: boolean
  className?: string
}) {
  const { isSelected, className } = opts || {}
  return cn(
    tableStyles.row.base,
    isSelected ? tableStyles.row.selected : '',
    className,
  )
}

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  isSelected?: boolean
  isHeader?: boolean
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ isSelected, className, isHeader, ...props }, ref) => {
    return (
      <tr
        ref={ref}
        className={getTableRowClassName({ isSelected, className })}
        {...props}
      />
    )
  },
)
TableRow.displayName = 'TableRow'

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      `h-10 px-4 text-left align-middle font-semibold text-table-th-text [&:has([role=checkbox])]:pr-0 
      [&>[role=checkbox]]:translate-y-[2px] text-sm`,
      className,
    )}
    {...props}
  />
))
TableHead.displayName = 'TableHead'

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'py-2 px-4 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className,
    )}
    {...props}
  />
))
TableCell.displayName = 'TableCell'

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-muted-foreground', className)}
    {...props}
  />
))
TableCaption.displayName = 'TableCaption'

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
